import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import ButtonFill from '../ui/ButtonFill';
import ButtonIcon from '../ui/ButtonIcon';
import { useGSAP } from '@gsap/react';
import gsap from "gsap";

export const useSelectedSnapDisplay = (emblaApi) => {
  const [selectedSnap, setSelectedSnap] = useState(0)
  const [snapCount, setSnapCount] = useState(0)

  const updateScrollSnapState = useCallback((emblaApi) => {
    setSnapCount(emblaApi.scrollSnapList().length)
    setSelectedSnap(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    updateScrollSnapState(emblaApi)
    emblaApi.on('select', updateScrollSnapState)
    emblaApi.on('reInit', updateScrollSnapState)
  }, [emblaApi, updateScrollSnapState])

  return {
    selectedSnap,
    snapCount
  }
}

const HomeNews = ({title, text, news, buttonText, buttonLink}) => {

const [emblaRef, emblaApi] = useEmblaCarousel({ 
  align: 'start',  
  breakpoints: {
      '(min-width: 1280px)': { slidesToScroll: 2, containScroll: false,}
    }

});


const { selectedSnap, snapCount } = useSelectedSnapDisplay(emblaApi);



  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

const scrollNext = useCallback(() => {
    if (emblaApi && emblaApi.canScrollNext()) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  const sectionRef = useRef(null);      
  const titleRef = useRef(null);
  const textRef = useRef(null);
  const buttonWrapper = useRef(null);
  const sliderWrapper = useRef(null);
const itemsRefs = useRef([]);
  const containerRef = useRef(null)


  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1280px)", () => {


            gsap.fromTo(containerRef.current, {
              y: '14rem'
            },
                {
              y: 0,
              ease: "none",
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top bottom", 
                end: "top 65%",  
                scrub: 0.1,  
              },
            });
      

      gsap.fromTo(titleRef.current, { opacity: 0 }, {
        opacity: 1,

        duration: 1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 60%",
        },
      });

      gsap.fromTo(textRef.current, { opacity: 0}, {
        opacity: 1,
        duration: 1,
        delay: 0.2,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 60%",
        },
      });

      gsap.fromTo(buttonWrapper.current, { opacity: 0}, {
        opacity: 1,
        duration: 1,
        delay: 0.4,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 60%",
        },
      });



      itemsRefs.current.forEach((item, index) => {

        const imageElement = item?.querySelector('img'); 



        if (imageElement) { 
            gsap.fromTo(imageElement, {
              y: '-3rem',
            }, {
              y: '3rem', 
              ease: "none",
              scrollTrigger: {
                trigger: sliderWrapper.current, 
                start: "top bottom",
                end: "bottom start",
                scrub: 0, 
              },
            });
          }

      });


    });

    return () => mm.revert();
  }, {scope: sectionRef, dependencies: news});


  return (
    <section ref={sectionRef} className="flex container-max flex-col w-full pb-[4rem] pt-[5rem] md:pb-[5rem] md:pt-[6rem] lg:pb-[6rem] lg:pt-[8rem] xl:pb-[8rem]  xl:pt-[10rem]">
      <div className='flex flex-col w-full' ref={containerRef}>

      <div className='mb-[2.4rem] md:mb-[4rem] md:flex lg:flex-col lg:items-start md:items-end container-padding'>
        <div className='w-full mb-[2.4rem] md:mb-0 lg:mb-[4rem]'>
          <h2 ref={titleRef} className='mb-[2rem] lg:mb-[3.2rem]'>{title}</h2>
          <p ref={textRef} className='text-1 md:pr-[7rem] md:max-w-[55rem]'>{text}</p>
        </div>
        
        <div ref={buttonWrapper}>
          <ButtonFill isLink={true} loading='lazy' href={buttonLink} className='md:w-fit text-nowrap'>{buttonText}<img fetchPriority="low" className="ml-[1.2rem] -rotate-45" src="/images/arrow_right.svg" alt="Стрелка вправо" /></ButtonFill>
        </div>
      </div>

      
      <div ref={sliderWrapper} className="embla container-padding mb-[2.4rem]" >
      <div className='hidden items-center lg:flex justify-end mb-[3.2rem] w-full'>
          
          <ButtonIcon onClick={scrollPrev} isDisabled={selectedSnap === 0} ><svg className='rotate-180' width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 7H12M12 7L6 1M12 7L6 13" stroke="CurrentColor"/>
                        </svg></ButtonIcon>
              
              {/* This will now show 1/2 instead of 1/4 automatically */}
              <div className="heading-4 embla__selected-snap-display w-[11.2rem] xl:w-[16rem] flex items-center justify-center">
                {selectedSnap + 1}/{snapCount}
              </div>
          
          {/* 3. UPDATE: Logic to disable button when on the last 'page' */}
          <ButtonIcon onClick={scrollNext} isDisabled={selectedSnap === snapCount - 1} ><svg className='' width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 7H12M12 7L6 1M12 7L6 13" stroke="CurrentColor"/>
                        </svg></ButtonIcon>
        </div>


        <div className='embla__viewport' ref={emblaRef}>
          <div className="embla__container  gap-[1.2rem] md:gap-[1.6rem] lg:gap-[4.2%]">
            {news.map((item, index) => (
              <div key={index} ref={el => {
          if (el) {
            itemsRefs.current[index] = el;
          }
        }} className="flex flex-col w-[calc(100%-4rem)] md:flex-row items-stretch embla__slide flex-[0_0_31.6rem]  md:flex-[0_0_59.6rem] lg:flex-[0_0_50%]">
                <div className="relative aspect-[1.44/1] flex-shrink-0 flex overflow-hidden items-center justify-center md:w-[22.9rem] lg:w-[34.2%] xl:w-[31rem] md:aspect-[0.8] w-full">
                  <div className='h-[120%] relative w-full'>
                    <Image
                      src={item.image.url}
                      alt={item.image.alt}
                      fill
                      sizes="400px"
                      className="w-auto object-cover"
                    />
                  </div>

                </div>

                <div className="flex-1 md:flex-initial bg-dusty-green-light md:bg-transparent p-[2rem] lg:px-[2.4rem] xl:px-[3.2rem] flex flex-col justify-between md:justify-start md:pt-[4rem]">
                  <div>
                  <div className='flex mb-[1.2rem] lg:justify-start lg:gap-[5.6rem] w-full justify-between items-end'>
                    <p className='caption text-dark-blue opacity-40'>{item.date}</p>
                    <p className='caption text-dark-blue opacity-40 whitespace-nowrap w-[13rem] xl:w-[15.4rem]'>{item.duration}</p>
                  </div>
    <h3 className="mb-[2.4rem] heading-4 max-w-[70rem] text-dark-blue md:mb-[4rem]">
      {/* Small screens: truncated */}
      <span className="block md:hidden">
        {item.title.length > 70
          ? item.title.slice(0, item.title.slice(0, 70).lastIndexOf(' ')) + '…'
          : item.title}
      </span>

      {/* Medium+ screens: full */}
      <span className="hidden md:block">{item.title}</span>
    </h3>                  </div>

                  <a href={item.link} className=" hover:opacity-80 transition-opacity text-link text-dark-blue">Читать статью</a>
                </div>
              </div>
            ))}
          </div>
        </div>


        
    </div>






      </div>
    </section>
  );
};

export default HomeNews;